import React, { useState, useEffect, useMemo } from 'react';
import { TextV2, Box, Flex, JoyKitThemeProvider, SelectV1, OptionType, ButtonV2, FormControl, InputV2 } from '@withjoy/joykit';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import 'react-intl-tel-input/dist/main.css';
import { useTranslation } from '@shared/core';
import { isValidEmail } from '@shared/utils/emailValidation';
import { useReminderDurationOptions, ReminderModalDurationType, DEV_REMINDER_MINUTES_PER_WEEK } from './ReminderModal.constants';
import { addWeeks, isAfter } from 'date-fns';
import { JOY_TERMS_URL, JOY_PRIVACY_URL } from '@shared/utils/constants';
import { useCreateRemindMeLaterEmailMutation } from '@graphql/generated';
import { useGuestData } from '@shared/hooks/useGuestData';
import { CheckOutline, HotelProduct } from '@withjoy/joykit/icons';
import { getConfig } from '@config';

// Define the telemetry interface for the modal
interface ReminderModalTelemetry {
  remindMeLaterDialog: {
    enter: () => void;
  };
  formFieldInteracted: (label: string, inputAction: 'onFocus' | 'onChange') => void;
  remindMeLaterFormSubmitted: (duration: ReminderModalDurationType, reminderDate: string) => void;
  remindMeLaterDialogDismissed: () => void;
  remindMeLaterDialogOutsideClick: () => void;
}

interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventDate?: Date | null;
  eventId?: string;
  telemetry: ReminderModalTelemetry;
}

export const ReminderModal: React.FC<ReminderModalProps> = ({ isOpen, onClose, eventDate, eventId, telemetry }) => {
  const { t } = useTranslation('guestSiteCustom');
  const reminderModal = t('reminderModal');
  const { terms, privacyPolicy, consentTextEmail, title, subtitle, sendReminder, emailPlaceholder, emailErrorText, setReminder, noThanks, emailLabel, success } = reminderModal;
  const allReminderDurationOptions = useReminderDurationOptions();

  const [createRemindMeLaterEmail, { loading, data }] = useCreateRemindMeLaterEmailMutation();

  const { person } = useGuestData({ eventId });

  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const isDevelopment = getConfig().environment === 'dev';

  // Filter week options based on event date
  const filteredReminderDurationOptions = useMemo(() => {
    if (!eventDate) {
      return allReminderDurationOptions;
    }

    const now = new Date();
    const sixDaysBeforeEvent = new Date(eventDate);
    sixDaysBeforeEvent.setDate(sixDaysBeforeEvent.getDate() - 6);

    return allReminderDurationOptions.filter(option => {
      const weekValue = parseInt(option.value as string);
      const reminderDate = addWeeks(now, weekValue);

      // Only show options where the reminder date is at least 6 days before the event date
      return !isAfter(reminderDate, sixDaysBeforeEvent);
    });
  }, [allReminderDurationOptions, eventDate]);

  const [selectedWeek, setSelectedWeek] = useState<OptionType | null>(filteredReminderDurationOptions[0]);

  useEffect(() => {
    if (person?.email) {
      setEmail(person.email);
      setIsEmailValid(isValidEmail(person.email));
    }
  }, [person]);

  // Track page view when modal is opened
  useEffect(() => {
    if (isOpen) {
      telemetry.remindMeLaterDialog.enter();
    }
  }, [isOpen, telemetry.remindMeLaterDialog]);

  useEffect(() => {
    if (filteredReminderDurationOptions.length > 0 && !filteredReminderDurationOptions.find(option => option.value === selectedWeek?.value)) {
      setSelectedWeek(filteredReminderDurationOptions[0]);
    }
  }, [filteredReminderDurationOptions, selectedWeek]);

  // Handle mutation success
  useEffect(() => {
    if (!loading && 'createRemindMeLaterEmail' in (data || {})) {
      setIsSuccess(true);
    }
  }, [loading, data]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handleEmailFocus = () => {
    telemetry.formFieldInteracted('email', 'onFocus');
  };

  const handleEmailBlur = () => {
    const isValid = isValidEmail(email);
    setIsEmailValid(isValid);

    if (!isValid && email) {
      setEmailError(emailErrorText());
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async () => {
    if (!isEmailValid || !email) {
      setEmailError(emailErrorText());
      return;
    }

    const sendReminderValue = selectedWeek?.label as ReminderModalDurationType;
    const weekValue = parseInt(selectedWeek?.value as string);

    let reminderDate: Date;
    if (isDevelopment) {
      // In development, use minutes instead of weeks
      const minutesToAdd = weekValue * DEV_REMINDER_MINUTES_PER_WEEK;
      reminderDate = new Date(Date.now() + minutesToAdd * 60 * 1000);
    } else {
      // In production, use weeks
      reminderDate = addWeeks(new Date(), weekValue);
    }

    telemetry.remindMeLaterFormSubmitted(sendReminderValue, reminderDate.toISOString());

    await createRemindMeLaterEmail({
      variables: {
        data: {
          email,
          eventId: eventId || '',
          reminderDate: reminderDate.toISOString()
        }
      }
    });
  };

  const handleCancel = () => {
    telemetry.remindMeLaterDialogDismissed();
    onClose();
  };

  const handleSuccessClose = () => {
    onClose();
  };

  const handleOutsideClick = () => {
    if (!isSuccess) {
      telemetry.remindMeLaterDialogOutsideClick();
    }
    onClose();
  };

  const renderFooter = () => {
    if (isSuccess) {
      return (
        <Box display="flex" flexDirection="column" gap={5} width="100%">
          <ButtonV2 variant="solid" intent="newPrimary" shape="rounded" onClick={handleSuccessClose} width="100%" fontWeight="semibold">
            {success.doneButton()}
          </ButtonV2>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={5} width="100%">
        <ButtonV2 intent="newPrimary" onClick={handleSubmit} shape="rounded" width="100%" loading={loading} fontWeight="semibold">
          {setReminder()}
        </ButtonV2>
        <ButtonV2 variant="ghost" intent="neutral" onClick={handleCancel} width="100%" fontWeight="semibold">
          {noThanks()}
        </ButtonV2>
      </Box>
    );
  };

  const renderContent = () => {
    if (isSuccess) {
      const weekValue = parseInt(selectedWeek?.value as string);
      return (
        <Flex flexDirection="column" gap={5} alignItems="center" textAlign="center">
          <CheckOutline size={36} />
          <Flex flexDirection="column" gap={3}>
            <TextV2 typographyVariant="hed5" fontWeight="semibold">
              {success.title()}
            </TextV2>
            <TextV2 typographyVariant="body3">{success.subtitle.text({ count: weekValue })}</TextV2>
          </Flex>
        </Flex>
      );
    }

    return (
      <Flex flexDirection="column" gap={6}>
        <Flex flexDirection="column" gap={5} alignItems="center">
          <HotelProduct size={48} />
          <Flex flexDirection="column" gap={3} textAlign="center">
            <TextV2 typographyVariant="hed5" fontWeight="semibold">
              {title()}
            </TextV2>
            <TextV2 typographyVariant="body3">{subtitle()}</TextV2>
          </Flex>
        </Flex>
        <Flex flexDirection="column" gap={5}>
          <Box display="flex" flexDirection="column" gap={3}>
            <TextV2 typographyVariant="label3">{sendReminder()}</TextV2>
            <SelectV1
              searchable={false}
              options={filteredReminderDurationOptions}
              value={selectedWeek}
              onMenuOpen={() => {
                telemetry.formFieldInteracted('send reminder', 'onFocus');
              }}
              onChange={value => {
                setSelectedWeek(value);
              }}
            />
          </Box>

          <FormControl label={emailLabel()} error={emailError} overrides={{ Hint: { props: { color: 'negative6' } } }}>
            <InputV2
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={e => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              onFocus={handleEmailFocus}
              autoComplete="email"
              placeholder={emailPlaceholder()}
            />
          </FormControl>

          <TextV2 typographyVariant="label1" color="mono10" letterSpacing={-0.032} lineHeight={1.4} textAlign="center">
            {consentTextEmail({
              terms: (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a href={JOY_TERMS_URL} target="_blank" style={{ textDecoration: 'underline' }}>
                  {terms()}
                </a>
              ),
              privacyPolicy: (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a href={JOY_PRIVACY_URL} target="_blank" style={{ textDecoration: 'underline' }}>
                  {privacyPolicy()}
                </a>
              )
            })}
          </TextV2>
        </Flex>
      </Flex>
    );
  };

  return (
    <JoyKitThemeProvider>
      <ResponsiveDialog
        dialogSize="sm"
        isOpen={isOpen}
        onClose={handleOutsideClick}
        renderFooter={renderFooter}
        hasCloseButton={false}
        removeBodyPadding={true}
        drawerOverrides={{
          Body: { props: { padding: 6, maxHeight: '100vh' } },
          Content: { props: { overflow: 'auto', height: 'fit-content', maxHeight: '100dvh', borderTopLeftRadius: 4, borderTopRightRadius: 4 } }
        }}
        dialogOverrides={{ Body: { props: { paddingX: 6 } } }}
      >
        {renderContent()}
      </ResponsiveDialog>
    </JoyKitThemeProvider>
  );
};
